<template>
  <v-menu
    v-if="$vuetify.display.mdAndDown"
    v-model="showMenu"
    :close-on-content-click="false"
    location="bottom start"
  >
    <template #activator="{ props }">
      <NotificationBadge :notifications-length="total">
        <v-btn
          size="small"
          icon
          v-bind="props"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>
      </NotificationBadge>
    </template>
    <v-card min-width="320">
      <!-- the full profile menu inline, with notifications under the user row:
           a profile row that opened a second menu hid half of it -->
      <ProfileMenuContent @close="showMenu = false">
        <v-divider />
        <NotificationMenu
          :notifications="notifications"
          :total="total"
          :has-more="hasMore"
          :loading="loading"
          @load:more="loadMore"
          @updateNotification="updateNotificationHandler"
          @clearAllNotifications="clearAllNotificationsHandler"
          @clearNotification="clearNotificationHandler"
        />
      </ProfileMenuContent>
    </v-card>
  </v-menu>
  <div v-else class="d-flex align-center">
    <NotificationMenu
      :notifications="notifications"
      :total="total"
      :has-more="hasMore"
      :loading="loading"
      @load:more="loadMore"
      @updateNotification="updateNotificationHandler"
      @clearAllNotifications="clearAllNotificationsHandler"
      @clearNotification="clearNotificationHandler"
    />
    <ProfileMenu />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { RequestAbortMixin } from '@/bundles/App/mixins/RequestAbortMixin';
import { notificationService } from '@/bundles/Notification/factories/notificationServiceFactory';
import { INotification } from '@/bundles/Notification/interfaces/INotification';
import { SocketEventTypesEnum } from '@/helpers/sockets/event-types';
import { socketInstance } from '@/plugins/socket/socket';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import NotificationMenu from '@/bundles/Panels/components/NotificationMenu.vue';
import NotificationBadge from '@/bundles/Panels/components/NotificationBadge.vue';
import ProfileMenu from '@/bundles/Panels/components/ProfileMenu.vue';
import ProfileMenuContent from '@/bundles/Panels/components/ProfileMenuContent.vue';

const NOTIFICATIONS_PAGE_SIZE = 20;

interface IProfileMenuWrapper {
  showMenu: boolean;
  notifications: INotification[];
  total: number;
  loading: boolean;
}

export default defineComponent({
  name: 'ProfileMenuWrapper',

  components: { ProfileMenu, ProfileMenuContent, NotificationMenu, NotificationBadge },
  mixins: [RequestAbortMixin],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: (): IProfileMenuWrapper => ({
    showMenu: false,
    notifications: [],
    total: 0,
    loading: false,
  }),

  computed: {
    hasMore (): boolean {
      return this.notifications.length < this.total;
    },
  },

  created () {
    this.resetNotifications();
    socketInstance?.on(SocketEventTypesEnum.REFRESH_NOTIFICATIONS, this.resetNotifications);
  },

  beforeUnmount () {
    socketInstance?.off(SocketEventTypesEnum.REFRESH_NOTIFICATIONS, this.resetNotifications);
    this.abortController?.abort();
  },

  methods: {
    resetNotifications () {
      this.notifications = [];
      this.total = 0;
      this.loadMore();
    },
    async loadMore () {
      if (this.loading) return;

      if (this.notifications.length > 0 && !this.hasMore) return;

      this.handleRequestAbort();
      this.loading = true;

      try {
        const { data, total } = await notificationService.getMyNotifications({
          take: NOTIFICATIONS_PAGE_SIZE,
          skip: this.notifications.length,
          controller: this.abortController,
        });

        this.notifications = [...this.notifications, ...data];
        this.total = total;
      } catch (error: any) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.loading = false;
      }
    },
    updateNotificationHandler (notification: INotification) {
      this.notifications = this.notifications.map(tempNotification => {
        if (tempNotification.id === notification.id) {
          return notification;
        }

        return tempNotification;
      });
    },
    clearNotificationHandler (id: number) {
      this.notifications = this.notifications.filter((item) => item.id !== id);
      this.total = Math.max(0, this.total - 1);
    },
    clearAllNotificationsHandler () {
      this.notifications = [];
      this.total = 0;
    }
  }
})
</script>
